import { FunctionComponent, useDeferredValue, useMemo } from 'react';
import { ICoordinatingGroup, ICountry, IInstitution, IMasterGroup, ITrial, SourceDataVerificationPatient } from '@/api/dtos';
import { Divider, List, LinearProgress, Box, styled } from '@mui/material'
import PatientListItemSkeleton from './PatientListItemSkeleton';
import PatientListItem from './PatientListItem';
import { Virtuoso } from 'react-virtuoso';
import { BasicAlert } from '@ngt/forms';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import OverlayLoading from '@/components/common/overlayLoading/OverlayLoading';

const ProgressContainer = styled('div')({
    position: 'relative'
});

const DataProgress = styled(LinearProgress)({
    position: 'absolute',
    left: 0,
    right: 0
});

interface IPatientList {
    trial?: ITrial;
    patients?: SourceDataVerificationPatient[];
    institutions?: IInstitution[];
    countries?: ICountry[];
    masterGroups?: IMasterGroup[];
    coordinatingGroups?: ICoordinatingGroup[];
    loading?: boolean;
    updating?: boolean;
    skeletonCount?: number;

    selectedMasterGroupId?: number;
    selectedCoordinatingGroupId?: number;
    selectedCountryId?: number;
    selectedInstitutionId?: number;
    selectedPatientId?: number;
}

interface IPatientRecord {
    patient?: SourceDataVerificationPatient;
    institution?: IInstitution;
    country?: ICountry;
    masterGroup?: IMasterGroup;
    coordinatingGroup?: ICoordinatingGroup;
}

const PatientList: FunctionComponent<IPatientList> = ({
    patients,
    institutions,
    countries,
    coordinatingGroups,
    masterGroups,
    trial,
    loading,
    updating,
    skeletonCount,
    selectedMasterGroupId,
    selectedCoordinatingGroupId,
    selectedCountryId,
    selectedInstitutionId,
    selectedPatientId
}) => {
    const loadingArray = useMemo(() => {
        return Array(0).fill(0);
    }, [])

    const patientArray = useMemo(() => {
        return patients?.map((patient) => {
            const institution = institutions?.find(y => y.id === patient.institutionId);
            const country = countries?.find(y => y.id === institution?.countryId);
            const masterGroup = masterGroups?.find(y => y.id === institution?.masterGroupId);
            const coordinatingGroup = coordinatingGroups?.find(y => y.id === institution?.coordinatingGroupId);

            if (
                (!selectedMasterGroupId || institution?.masterGroupId === selectedMasterGroupId) && 
                (!selectedCoordinatingGroupId || institution?.coordinatingGroupId === selectedCoordinatingGroupId) && 
                (!selectedCountryId || institution?.countryId === selectedCountryId) && 
                (!selectedInstitutionId || institution?.id === selectedInstitutionId) &&
                (!selectedPatientId || patient?.id === selectedPatientId)
            ) {
                return {
                    patient,
                    institution,
                    masterGroup,
                    coordinatingGroup,
                    country
                } as IPatientRecord
            }

            return null;
        }).filter(x => !!x) ?? [];
    }, [patients, institutions, countries, masterGroups, coordinatingGroups, selectedMasterGroupId, selectedCoordinatingGroupId, selectedCountryId, selectedInstitutionId, selectedPatientId])

    return (

        
        <>

          {loading ? (
                <OverlayLoading />
            ) : (
            <>
                {
                    updating && (
                        <ProgressContainer>
                            <DataProgress
                                color="secondary"
                            />
                        </ProgressContainer>
                    )
                }
                <List disablePadding>
                    {
                        (loading || !!patientArray?.length) && (
                            <Virtuoso
                                useWindowScroll
                                increaseViewportBy={400}
                                data={loading ? loadingArray : patientArray}
                                itemContent={(index, patient) => {
                                return (
                                    <>
                                        <PatientListItem  
                                            institution={patient.institution}
                                            patient={patient.patient}
                                            trial={trial}
                                        />
                                        { index < ((patients?.length ?? 0) - 1) && <Divider />}
                                    </>
                                )
                                }}
                            />
                        )
                    }
                </List>
            </>
            )}
        </>
    );
}

export default PatientList;